// @flow
import * as React from 'react';
import * as Yup from 'yup';
import cn from 'classnames';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Components
import { Container } from 'reactstrap';
import { Form, Formik, FormikActions } from 'formik';
import { TextField, FormikField, PasswordField } from 'components/api';
import { Button, Icon } from 'components/atoms/';
import InvisibleRecaptcha from 'components/molecules/InvisibleRecaptcha';

// Types
import { gtmUserEventType } from 'types/gtm';

import './SignUp.scss';

// Utils
import { isObjEmpty } from 'utils/varValidation';
import { EMAIL_REGEX, USERNAME_REGEX } from 'utils/regex';
import { clickEvent } from 'utils/adaConfigurations/customEvents';

// Constants
import { GTM_API, GTM_RECAPTCHA_EVENTS } from 'constants/recaptcha';

const { registerUser } = GTM_API;

export type Props = {|
  /**
   * Callback invoked when the arrow is clicked
   */
  onArrowClick: (event: MouseEvent) => void,
  /**
   * Default value for userName on sign up is the email provided
   */
  email?: string,
  /**
   * User event for google tag manager
   */
  gtmUserEvent: gtmUserEventType,
  /**
   * Sign up action
   */
  signUp: (userName: string, password: string) => Object,
  setRecaptchaToken: (token: string) => void,
  isRecaptchaEnabledOnRegisterUser: boolean,
  gtmRecaptchaEvent: (event: string, api: string, token: string) => void,
  languageCode: string,
  intl: IntlShape
|};

type Values = {
  userName: string,
  password: string
};

type State = {
  formikValues: Values,
  formikActions: FormikActions<Values>
};

Yup.addMethod(Yup.string, 'username', function(message) {
  return this.test({
    message: message || 'Invalid username',
    test(value?: string = '') {
      return EMAIL_REGEX.test(value) || USERNAME_REGEX.test(value);
    }
  });
});

const validationSchema = Yup.object().shape({
  userName: Yup.string()
    .required()
    .username(),
  password: Yup.string()
    .required()
    .min(6)
});

class SignUp extends React.Component<Props, State> {
  static block = 'sign-up';

  static defaultProps = {
    email: ''
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      formikValues: {},
      formikActions: {},
      isListeningForRecaptcha: false
    };
    (this: any).userNameInput = React.createRef();
    (this: any).passwordInput = React.createRef();
    (this: any).onSubmit = this.onSubmit.bind(this);
    (this: any).onPreSubmit = this.onPreSubmit.bind(this);
    if (props.isRecaptchaEnabledOnRegisterUser) {
      (this: any).recaptcha = React.createRef();
    }
  }

  initialValues = {
    userName: '',
    password: ''
  };
  userNameInput: React.Ref<'input'>;
  passwordInput: React.Ref<'input'>;

  async onPreSubmit(values: Values, actions: FormikActions<Values>) {
    const { isRecaptchaEnabledOnRegisterUser } = this.props;
    const { isListeningForRecaptcha } = this.state;
    this.setState({
      formikValues: values,
      formikActions: actions
    });
    if (isRecaptchaEnabledOnRegisterUser) {
      !isListeningForRecaptcha &&
        this.recaptcha.current.initRecaptchaListener();
      this.setState({ isListeningForRecaptcha: true });
      this.recaptcha.current.execute();
    } else {
      this.onSubmit(values, actions);
    }
  }

  async onSubmit() {
    (this: any).userNameInput.current.blur();
    (this: any).passwordInput.current.blur();
    const { formikValues, formikActions } = this.state;
    const { userName, password } = formikValues;
    const { setSubmitting, setStatus } = formikActions;
    const {
      signUp,
      gtmUserEvent,
      isRecaptchaEnabledOnRegisterUser,
      recaptchaToken,
      gtmRecaptchaEvent
    } = this.props;
    const data = await signUp(userName, password);
    const success = data && data.success;
    !success && this.recaptcha && this.recaptcha.current.reset();

    setSubmitting(false);
    setStatus({ success });
    gtmUserEvent(
      'Sign Up Button',
      'Clicked',
      success ? 'Sign Up' : 'error Sign Up',
      success ? 'GTM_CURRENT_PAGE > User Vehicles Page' : 'GTM_CURRENT_PAGE'
    );
    isRecaptchaEnabledOnRegisterUser &&
      gtmRecaptchaEvent(
        GTM_RECAPTCHA_EVENTS.API_CALL,
        registerUser,
        recaptchaToken,
        success
      );
  }

  render() {
    const {
      intl,
      email,
      isRecaptchaEnabledOnRegisterUser,
      languageCode,
      setRecaptchaToken,
      gtmRecaptchaEvent
    } = this.props;
    this.initialValues.userName = email || '';
    const ns = SignUp.block;
    return (
      <div className={ns}>
        <div
          className={`${ns}__sign-up`}
          onClick={this.props.onArrowClick}
          onKeyUpCapture={clickEvent}
        >
          <Icon
            type="chevron__up"
            tabIndex={0}
            aria-label={intl.formatMessage({
              id: 'c8.aria.return.google.login',
              defaultMessage: 'Return to Google login'
            })}
          />
        </div>
        <h1 className={`${ns}__title`} aria-disabled={true}>
          {intl.formatMessage({ id: 'c8.common.sign.up' })}
        </h1>
        <Formik
          initialValues={this.initialValues}
          validationSchema={validationSchema}
          onSubmit={this.onPreSubmit}
          render={({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            dirty,
            status
          }) => {
            const disabled = !dirty || !isObjEmpty(errors);
            const submitSuccess = !status || status.success;
            const errorClassProps = cn(`${ns}__invalid`, {
              invisible: submitSuccess
            });
            const userNameProps = {};
            if (!submitSuccess) {
              userNameProps.isInvalid = true;
            }
            return (
              <Container className={ns}>
                <Form onSubmit={handleSubmit}>
                  <div className={`${ns}__form-fields`}>
                    <FormikField
                      renderedComponent={TextField}
                      className={`${ns}__username-field`}
                      name="userName"
                      placeholder={intl.formatMessage({
                        id: 'c8.common.username'
                      })}
                      htmlRef={this.userNameInput}
                      {...userNameProps}
                      tabIndex={0}
                      aria-label={intl.formatMessage({
                        id: 'c8.aria.user.name',
                        defaultMessage: 'User Name'
                      })}
                    />
                    <div className={errorClassProps}>
                      {intl.formatMessage({
                        id:
                          'c8.confirmation.modal.create.profile.username.already.taken'
                      })}
                    </div>
                    <FormikField
                      renderedComponent={PasswordField}
                      name="password"
                      placeholder={intl.formatMessage({
                        id: 'c8.common.password'
                      })}
                      htmlRef={this.passwordInput}
                      tabIndex={0}
                      aria-label={intl.formatMessage({
                        id: 'c8.aria.password',
                        defaultMessage: 'Password'
                      })}
                    />
                  </div>
                  <Button
                    className={`${ns}__sign-up-btn`}
                    size="medium"
                    disabled={disabled}
                    type="Submit"
                    tabIndex={0}
                  >
                    <div className={`${ns}--search-button`}>
                      {intl.formatMessage({ id: 'c8.common.sign.up' })}
                      {isSubmitting && (
                        <div className="ld ld-ring ld-spin-fast" />
                      )}
                    </div>
                  </Button>
                </Form>
                {isRecaptchaEnabledOnRegisterUser && (
                  <InvisibleRecaptcha
                    setRecaptchaToken={setRecaptchaToken}
                    languageCode={languageCode}
                    gtmAction={gtmRecaptchaEvent}
                    gtmApi={registerUser}
                    onResolveAction={async () => {
                      this.setState({
                        isListeningForRecaptcha: false
                      });
                      await this.onSubmit();
                    }}
                    ref={this.recaptcha}
                  />
                )}
              </Container>
            );
          }}
        />
      </div>
    );
  }
}

export default SignUp;



// WEBPACK FOOTER //
// ./src/components/pages/ConfirmationPage/CreateProfile/components/SignUp/SignUp.js